import { useMemo } from 'react';

import { isServiceOrder } from '#domain/Order.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';
import { filterStopInSearch } from '#utils/searchUtils.ts';

const containsSearchTerm = (item: OrderWithSortableKey, search: string) => {
    const text = search.trim().toLowerCase();
    return (
        item.number?.toLowerCase().includes(text) ||
        item.customerName?.toLowerCase().includes(text) ||
        item.tourName?.toLowerCase().includes(text) ||
        item.truck?.name?.toLowerCase().includes(text) ||
        (isServiceOrder(item)
            ? item.description.toLowerCase().includes(text) || filterStopInSearch(text, item.executionStop)
            : filterStopInSearch(text, item.loadingStop) || filterStopInSearch(text, item.unloadingStop))
    );
};

const containsFilterTerm = (item: OrderWithSortableKey, statusFilter: string[]) => {
    return !item.status || statusFilter.length === 0 || statusFilter.includes(item.status);
};

const matchesFilters = (item: OrderWithSortableKey, statusFilter: string[], search: string) =>
    containsFilterTerm(item, statusFilter) && containsSearchTerm(item, search);

export const useFilteredOrders = (
    items: OrderWithSortableKey[],
    search: string,
    statusFilter: string[],
    selectedRowIds: string[],
    selectedOrderId?: string
) => {
    const filteredOrders = useMemo(
        () => items.filter(item => matchesFilters(item, statusFilter, search)),
        [items, search, statusFilter]
    );
    const selectedOrders = useMemo(
        () => filteredOrders.filter(order => selectedRowIds.includes(order.id)),
        [filteredOrders, selectedRowIds]
    );
    const selectableOrders = useMemo(() => filteredOrders.filter(order => order.status === 'open'), [filteredOrders]);
    const activeOrder = useMemo(() => items.find(order => order.id === selectedOrderId), [items, selectedOrderId]);

    return {
        filteredOrders,
        selectedOrders,
        selectableOrders,
        activeOrder,
    };
};
